import { html, LitElement } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { DESKTOP_BREAKPOINT_QUERY } from 'Utils/viewport';
import Styles from './vm-video-hub-related.scss';

import '../vm-video-hub-card/vm-video-hub-card';

/**
 * Responsive wrapper for a related-content section presented as a hub card.
 *
 * On desktop it wraps the slotted related content in a `vm-video-hub-card` (logo, title,
 * and CTA). On mobile it drops the card chrome and renders the related content on its own —
 * the hub card itself is surfaced separately on mobile.
 *
 * Props mirror `vm-video-hub-card`'s generalized `card-*` names and forward straight
 * through, so this wrapper is as agnostic as the card about what it points at.
 *
 * @slot - Default. The related-content list to render inside (desktop) or below (mobile) the hub card.
 */
@customElement('vm-video-hub-related')
export class VmVideoHubRelated extends LitElement {
    static override styles = Styles;

    @property({ type: String, attribute: 'card-title' }) cardTitle = '';
    @property({ type: String, attribute: 'card-image-src' }) cardImageSrc = '';
    @property({ type: String, attribute: 'card-image-alt' }) cardImageAlt = '';
    @property({ type: String, attribute: 'card-url' }) cardUrl = '';
    @property({ type: String, attribute: 'card-cta-label' }) cardCtaLabel!: string;
    /** The qualifying channel's slug, forwarded to the hub card for its Mixpanel events. */
    @property({ type: String, attribute: 'channel-slug' }) channelSlug = '';

    private _mediaQuery = window.matchMedia(DESKTOP_BREAKPOINT_QUERY);

    @state() private _isDesktop = this._mediaQuery.matches;

    private _onBreakpointChange = (e: MediaQueryListEvent) => {
        this._isDesktop = e.matches;
    };

    override connectedCallback() {
        super.connectedCallback();
        this._mediaQuery.addEventListener('change', this._onBreakpointChange);
    }

    override disconnectedCallback() {
        super.disconnectedCallback();
        this._mediaQuery.removeEventListener('change', this._onBreakpointChange);
    }

    override render() {
        return this._isDesktop
            ? html`
                  <vm-video-hub-card
                      card-title=${this.cardTitle}
                      card-image-src=${this.cardImageSrc}
                      card-image-alt=${this.cardImageAlt}
                      card-url=${this.cardUrl}
                      card-cta-label=${this.cardCtaLabel}
                      channel-slug=${this.channelSlug}
                  >
                      <slot slot="description"></slot>
                  </vm-video-hub-card>
              `
            : html`<slot></slot>`;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'vm-video-hub-related': VmVideoHubRelated;
    }
}
